// dependencies
import { format, isAfter, isValid, parseISO } from "date-fns";
import React from "react";

import { ICellRendererParams, Link, PYColumnDef, Text } from "@performyard/py-components";

//types
import { ReviewForm } from "../../../../../../api/services/searchServiceResultTypes/reviewForm";

//components
//utils
import get from "../../../../../../utils/GetNested";
import { appendCurrentLocationAsFrom } from "../../../../../../utils/reviewsBreadcrumbs";
import { arrayIncludes, findAuthorOrSigner } from "../../../utils";

const ViewButtonCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const report = params.data;
	if (!report) return null;
	return (
		<div className="flex h-full items-center">
			<Link
				href={appendCurrentLocationAsFrom(`/review/${report._id}/`)}
				underline="never"
				dangerouslySetClassName="inline-flex h-8 items-center justify-center rounded-1 border-none bg-primary-5 px-2 text-paragraph-3 font-semibold text-neutral-0 hover:bg-primary-7 active:bg-primary-8"
			>
				Open Form
			</Link>
		</div>
	);
};

const CompleteDateCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
	const report = params.data;
	if (!report) return null;
	const date = get(report, ["complete_date"], null);
	if (get(report, ["completed"]) === false) {
		return null;
	} else if (date) {
		const parsedDate = parseISO(date);
		if (isValid(parsedDate)) {
			return (
				<div className="flex h-full items-center">
					<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
						{format(parsedDate, "MMM d, yyyy h:mmaaa")}
					</Text>
				</div>
			);
		}
	}
	return null;
};

const createStatusCellRenderer = (userId: string) => {
	const StatusCellRenderer = (params: ICellRendererParams<ReviewForm>) => {
		const report = params.data;
		if (!report) return null;

		const submit = get(report, ["submit"], false);
		const startDate = get(report, ["start_date"], null);
		const completed = get(report, ["completed"], false);
		const redacted = get(report, ["redacted"], false);
		const authorTurn = get(report, ["author_turn"], 0);
		const signerTurn = get(report, ["signer_turn"], 0);
		const authors = get(report, ["author_objects"], []);
		const authorIDs = get(report, ["authors"], []);
		const signers = get(report, ["signer_objects"], []);
		const pastAuthors = get(report, ["past_authors"], null);
		const pastSigners = get(report, ["past_signers"], null);
		const currentSigner = get(report, ["signer"], null);
		const currentAuthor = get(report, ["author"], null);
		let status = "";

		if (completed) {
			return (
				<div className="flex h-full items-center">
					<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
						Completed
					</Text>
				</div>
			);
		}

		const parsedDate = parseISO(startDate);
		if (startDate && isAfter(parsedDate, new Date())) {
			status = `Starting ${format(parsedDate, "MMM d, yyyy h:mmaaa")}`;
			return (
				<div className="flex h-full items-center">
					<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
						{status}
					</Text>
				</div>
			);
		}

		if (
			!submit &&
			(authorTurn < authorIDs?.length ||
				arrayIncludes(
					authors.map((author) => author._id),
					"_redacted",
					0
				))
		) {
			if (arrayIncludes(pastAuthors, userId, 0)) {
				const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
				status = currentAuthorInfo?.name
					? `Awaiting submission by ${currentAuthorInfo.name} (Filled out the form)`
					: "Awaiting submission by Unassigned (Filled out the form)";
			} else if (
				arrayIncludes(authorIDs, userId, authorTurn) ||
				arrayIncludes(
					authors.map((author) => author._id),
					"_redacted",
					0
				)
			) {
				if (currentAuthor !== userId) {
					const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
					status = `Awaiting submission by ${
						redacted ? "Anonymous" : currentAuthorInfo?.name || "Unassigned"
					} (Fill out the form)`;
				} else {
					status = "Fill out the form";
				}
			} else {
				const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
				status = `Awaiting submission by ${
					redacted ? "Anonymous" : currentAuthorInfo.name || "Unassigned"
				} (Sign off the form)`;
			}
		} else if (signerTurn <= signers?.length) {
			if (currentSigner === userId) {
				status = "Sign off the form (Filled out the form)";
			} else if (arrayIncludes(pastAuthors, userId, 0)) {
				const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
				status = currentSignerInfo?.name
					? `Awaiting sign off by ${currentSignerInfo.name} (Filled out the form)`
					: "Awaiting sign off by Unassigned (Filled out the form)";
			} else if (arrayIncludes(pastSigners, userId, 0)) {
				const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
				status = `Awaiting sign off by ${currentSignerInfo?.name || "Unassigned"} (Signed off the form)`;
			} else if (currentSigner !== userId) {
				const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
				status = `Awaiting sign off by ${currentSignerInfo?.name || "Unassigned"}`;
			} else {
				status = "Sign off the form";
			}
		}

		return (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{status}
				</Text>
			</div>
		);
	};
	return StatusCellRenderer;
};

export const FormsIDidTableCols = (userId: string): PYColumnDef<ReviewForm>[] => {
	return [
		{
			headerName: "",
			field: "view",
			cellRenderer: ViewButtonCellRenderer,
			width: 116,
			sortable: false,
			resizable: false,
		},
		{
			headerName: "Subject",
			field: "subject",
			type: "employee",
			width: 166,
			sortable: false,
			resizable: false,
			valueGetter: (p) => ({
				_id: get(p.data, ["subject"], ""),
				name: get(p.data, ["subject_name"], ""),
			}),
		},
		{
			headerName: "Form",
			field: "form_name",
			type: "text",
			width: 380,
			sortable: false,
			resizable: false,
			valueGetter: (p) => p.data?.form_name ?? "",
		},
		{
			headerName: "Complete Date",
			field: "complete_date",
			cellRenderer: CompleteDateCellRenderer,
			width: 150,
			sortable: true,
			resizable: false,
		},
		{
			headerName: "Status",
			field: "status",
			cellRenderer: createStatusCellRenderer(userId),
			width: 280,
			sortable: false,
			resizable: false,
		},
	];
};

export default FormsIDidTableCols;
